import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import actions from '@elab/core/store/global/actions';
import { selectors } from '@elab/core/store/auth';

/**
 * @deprecated and used only on kruidvat. See ForgottenPasswordForm
 *
 * Component is used for initiate forgotten password flow
 *
 * @attr {string} success-redirect-url - Redirect link after success login
 * @attr {string} email-input-label - Label text for email input
 * @attr {string} email-input-placeholder - Placeholder text for email input
 * @attr {string} submit-button-text - Submit button text
 * @attr {string} global-error-message - Text for global message error
 * @attr {string} error-message - Error message for email input
 * @attr {string} empty-email-error-message - Error message for empty email input
 * @attr {string} bu-email-regexp - regular expression pattern for email field
 * @attr {string} token - CSRF token
 * @attr {bool} animated-label - Define is label animated
 * @attr {bool} show-error-icon - Define should error icon be displayed
 * @attr {bool} primary-button - Define should button be primary

 *
 * HTML Structure Example
 *
 * <e2-forgot-password-form
 *   success-redirect-url="page_url"
 *   email-input-label="Enter Email"
 *   email-input-placeholder="Enter Email"
 *   submit-button-text="Submit"
 *   error-message="Please enter valid Email"
 *   empty-email-error-message="Email is required"
 *   global-error-message="Please see error below"
 *   bu-email-regexp="^[a-zA-Z0-9._%+-]{1,60}@[a-zA-Z0-9.+-]+\.[a-zA-Z]{2,}$"
 *   token="CSRF_Token">
 * </e2-forgot-password-form>
 */

const DEFAULT_EMAIL_REGEXP = /^[a-zA-Z0-9._%+-]{1,60}@[a-zA-Z0-9.+-]+\.[a-zA-Z]+$/;
const INPUT_ERROR_CLASS = 'input--error';

export class ForgotPasswordForm extends SimpleLitElement {
    static get properties() {
        return {
            // Label of email input field
            emailInputLabel: {
                type: String,
                attribute: 'email-input-label',
            },
            // Placeholder of email input field
            emailInputPlaceholder: {
                type: String,
                attribute: 'email-input-placeholder',
            },
            // Predefined value of email input field
            emailInputValue: {
                type: String,
                attribute: 'email-input-value',
            },
            // Submit button text
            submitButtonText: {
                type: String,
                attribute: 'submit-button-text',
            },
            // URL to navigate after successful result
            successRedirectUrl: {
                type: String,
                attribute: 'success-redirect-url',
            },
            errorMessage: {
                type: String,
                attribute: 'error-message',
            },
            emptyEmailErrorMessage: {
                type: String,
                attribute: 'empty-email-error-message',
            },
            globalErrorMessage: {
                type: String,
                attribute: 'global-error-message',
            },
            buEmailRegexp: {
                type: String,
                attribute: 'bu-email-regexp',
            },
            token: {
                type: String,
            },
            isInError: {
                type: Boolean,
                attribute: false,
            },
            isLabelAnimated: {
                type: Boolean,
                attribute: 'animated-label',
            },
            showErrorIcon: {
                type: Boolean,
                attribute: 'show-error-icon',
            },
            isButtonPrimary: {
                type: Boolean,
                attribute: 'primary-button',
            },
            validationOnBlur: {
                type: Boolean,
                attribute: 'validation-on-blur',
            },
            username: { type: String, attribute: false },
            emailOnLoad: { type: Boolean, attribute: 'email-on-load' },
        };
    }

    constructor() {
        super();

        this.emailInputLabel = 'Je e-mailadres';
        this.emailInputPlaceholder = '';
        this.emailInputValue = '';
        this.submitButtonText = 'Verzenden';
        this.globalErrorMessage = '';
        this.buEmailRegexp = '';
        this.token = '';
        this.isLabelAnimated = false;
        this.showErrorIcon = false;
        this.isButtonPrimary = false;
        this.validationOnBlur = false;
        this.emptyEmailErrorMessage = '';
        this.username = '';
        this.emailOnLoad = false;
    }

    onInput(evt) {
        this.emailInputValue = evt.target.value;
        if (!this.validationOnBlur) {
            this.isInError = !this.isValidEmail(this.emailInputValue);
        }
    }

    onBlur({ target: { value: currentEmailValue } }) {
        if (this.emailOnLoad) {
            this.isInError = !this.isValidEmail(currentEmailValue);
        } else {
            this.isInError = !this.isValidEmail(this.emailInputValue);
        }
    }

    isValidEmail(value) {
        const emailRegexp = this.buEmailRegexp
            ? // eslint-disable-next-line elab-rules/detect-non-literal-regexp
              new RegExp(this.buEmailRegexp)
            : DEFAULT_EMAIL_REGEXP;
        return emailRegexp.test(value.toLowerCase());
    }

    onSubmit(e) {
        this.isInError = !this.isValidEmail(e.target[0].value);

        if (this.isInError) {
            e.preventDefault();
        }
    }

    getSubmitButtonClassList() {
        return classMap({
            button: true,
            'button--no-min-width': true,
            'button--full-width': true,
            'button--big': !this.isButtonPrimary,
            'button--primary': this.isButtonPrimary,
        });
    }

    render() {
        return html`
            <form
                action="${this.successRedirectUrl}"
                @submit="${this.onSubmit}"
                method="post"
                class="e2-forgot-password-form"
                id="forgottenPwdForm"
            >
                <div class="forgotten-password__input">
                    ${!this.isLabelAnimated
                        ? html`
                              <label class="label">${this.emailInputLabel}</label>
                          `
                        : ''}
                    <div class="input-wrapper">
                        <div class="input-inner">
                            <input
                                class="input ${this.isInError ? INPUT_ERROR_CLASS : ''}"
                                placeholder="${this.emailInputPlaceholder}"
                                type="text"
                                name="email"
                                @input="${this.onInput}"
                                @blur="${this.validationOnBlur && this.onBlur}"
                                value="${this.username || this.emailInputValue}"
                            />
                            ${this.isLabelAnimated
                                ? html`
                                      <label class="label">${this.emailInputLabel}</label>
                                  `
                                : ''}
                        </div>
                    </div>
                    ${this.isInError
                        ? html`
                              <span class="form-field-error">
                                  ${this.showErrorIcon
                                      ? html`
                                            <i class="icon icon-info"></i>
                                        `
                                      : ''}
                                  <span class="text">
                                      ${this.emailInputValue
                                          ? this.errorMessage
                                          : this.emptyEmailErrorMessage}</span
                                  >
                              </span>
                          `
                        : ''}
                </div>
                <input type="hidden" name="CSRFToken" value="${this.token}" />
                <div class="row">
                    <button type="submit" class=${this.getSubmitButtonClassList()}>
                        <span class="button__text">${this.submitButtonText}</span>
                        <span class="button__arrow_icon">
                            <i class="icon icon-arrow"></i>
                        </span>
                    </button>
                </div>
            </form>
        `;
    }
}

const mapStateToProps = state => ({
    username: selectors.getDataField(state, 'username'),
});

const mapDispatchToEvents = dispatch => ({
    setGlobalMessage: ({ detail }) => dispatch(actions.setGlobalError(detail.text, detail.type)),
});

export default () =>
    customElements.define(
        'e2-forgot-password-form',
        connect(mapStateToProps, mapDispatchToEvents)(ForgotPasswordForm),
    );
